/**
 * admin.js
 * Created by calhosh on 2/22/2022.
 */
import {API_RESOURCES, createApiReadAction, createApiCreateAction, createApiUpdateAction} from '../../common/utils/api';
import {normalize, setThousandSeparator} from '../../common/utils/utils';
import {produce} from 'immer';
import {HTTP_REQUEST_METHODS, userType, projectUserStatus} from '../../common/utils/uiconstants';
import {isEqual} from 'lodash';

export const initialState = {
	user: {},
	projects: {
		byId: {},
		allIds: []
	},
	users: {
		byId: {},
		allIds: []
	},
	administrators: {
		byId: {},
		allIds: []
	},
	editInProgress: {
		administrators: {
			byId: {},
			allIds: []
		},
		projects: {
			byId: {},
			allIds: []
		}
	},
	metadata: {
		projects: {
			hasChanges: false
		}
	}
};

const GET_ME = 'admin/me';
const GET_ADMIN_USERS = 'admin/getAll/user';
const GET_PROJECT_BY_ID = 'admin/get/project';
const CLEAR_PROJECT = 'admin/clear/project';
const GET_USER_INFO_BY_ID = 'admin/get/user';
const CLEAR_USER_INFO = 'admin/clear/user';
const CREATE_ADMIN_USER = 'admin/create/user';
const UPDATE_PROJECT = 'admin/update/project';
const RESET_PROJECT = 'admin/reset/project';
const UPDATE_PROJECTS_STORE = 'admin/user/update/project';
const UPDATE_ME = 'users/put';
const ADD_USER_PROJECT = 'admin/add/user/project';
const PATCH_ADMIN_USER = 'admin/patch/user';

export default function reducer(state = initialState, action) {
	switch (action.type) {
		case GET_ME: {
			return {
				...state,
				user: action.response
			};
		}

		case UPDATE_ME: {
			if (
				action.error ||
				!action.response ||
				action.response.userType === undefined ||
				action.response.userType === null ||
				action.response.userType.toLowerCase() !== userType.admin
			) {
				return state;
			}

			return {
				...state,
				user: {
					...action.response,
					...setThousandSeparator(action.response.numberFormat),
					systemAdministratorRoleIds: [...action.response.systemAdministratorRoleIds]
				}
			};
		}

		case GET_ADMIN_USERS: {
			if (action.error || !action.response) {
				return state;
			}

			const administrators = normalize(action.response, 'id', state.administrators);

			return {
				...state,
				administrators,
				editInProgress: {
					...state.editInProgress,
					administrators
				}
			};
		}

		case UPDATE_PROJECTS_STORE: {
			return updateProjectSlice(state, action);
		}

		case GET_PROJECT_BY_ID: {
			if (action.error || !action.response) {
				return state;
			}

			const projects = normalize([action.response], 'projectId', state.projects);

			return {
				...state,
				projects,
				editInProgress: {
					...state.editInProgress,
					projects
				}
			};
		}

		case UPDATE_PROJECT: {
			if (action.error || !action.response) {
				return state;
			}

			const projects = normalize([action.response], 'projectId', state.projects);

			return {
				...state,
				projects,
				editInProgress: {
					...state.editInProgress,
					projects
				},
				metadata: {
					...state.metadata,
					projects: {
						hasChanges: false
					}
				}
			};
		}

		case RESET_PROJECT: {
			return {
				...state,
				editInProgress: {
					projects: state.projects
				},
				metadata: {
					...state.metadata,
					projects: {
						hasChanges: false
					}
				}
			};
		}

		case CLEAR_PROJECT: {
			if (!state?.projects?.allIds?.includes(action.projectId)) {
				return state;
			}

			return {
				...state,
				projects: produce(state.projects, (draft) => {
					if (draft.byId[action.projectId]) {
						delete draft.byId[action.projectId];
					}

					draft.allIds = draft.allIds.filter((id) => id !== action.projectId);
				})
			};
		}

		case GET_USER_INFO_BY_ID: {
			if (action.error || !action.response || action.isValidate) {
				return state;
			}

			return {
				...state,
				users: normalize([action.response], 'userId', state.users)
			};
		}

		case CLEAR_USER_INFO: {
			if (!state?.users?.allIds?.includes(action.userId)) {
				return state;
			}

			return {
				...state,
				users: produce(state.users, (draft) => {
					if (draft.byId[action.userId]) {
						delete draft.byId[action.userId];
					}

					draft.allIds = draft.allIds.filter((id) => id !== action.userId);
				})
			};
		}

		case ADD_USER_PROJECT: {
			return {
				...state,
				editInProgress: {
					...state.editInProgress,
					projects: produce(state.editInProgress.projects, (draft) => {
						if (draft.byId[action.projectId]) {
							draft.byId[action.projectId].users.push({
								...action.user,
								status: projectUserStatus.active
							});
						}
					})
				},
				metadata: {
					...state.metadata,
					projects: {
						hasChanges: true
					}
				}
			};
		}

		case PATCH_ADMIN_USER:
		case CREATE_ADMIN_USER: {
			if (action.error || !action.response) {
				return state;
			}

			const administrators = normalize([action.response], 'id', state.administrators);

			return {
				...state,
				administrators,
				editInProgress: {
					...state.editInProgress,
					administrators
				}
			};
		}

		default:
			return state;
	}
}

function updateProjectSlice(state, action) {
	function updateProjectNameStatus(property) {
		const projects = produce(state.editInProgress.projects, (draft) => {
			if (draft.byId[action.projectId]) {
				draft.byId[action.projectId][property] = action.value;
			}
		});

		const editInProgress = {
			...state.editInProgress,
			projects
		};

		const metadata = {
			...state.metadata,
			projects: {
				hasChanges: !isEqual(projects.byId[action.projectId], state.projects.byId[action.projectId])
			}
		};

		return {
			...state,
			editInProgress,
			metadata
		};
	}

	function updateUserRoleStatus() {
		const projects = produce(state.editInProgress.projects, (draft) => {
			if (draft.byId[action.projectId]) {
				const project = draft.byId[action.projectId];
				project.users = project.users.map((user) => {
					if (user.userId === action.userId) {
						return {
							...user,
							[action.property]: action.value
						};
					}

					return user;
				});
			}
		});

		const editInProgress = {
			...state.editInProgress,
			projects
		};

		const metadata = {
			...state.metadata,
			projects: {
				hasChanges: !isEqual(projects.byId[action.projectId], state.projects.byId[action.projectId])
			}
		};

		return {
			...state,
			editInProgress,
			metadata
		};
	}

	switch (action.property) {
		case 'projectName':
			return updateProjectNameStatus(action.property);
		case 'projectStatus':
			return updateProjectNameStatus('status');

		case 'status':
		case 'isAdmin':
			return updateUserRoleStatus();
	}
}

export function getUserById(userId = 'Me') {
	return createApiReadAction(GET_ME, {[API_RESOURCES.USERS]: userId});
}

export function getAdminUsers() {
	return createApiReadAction(GET_ADMIN_USERS, {
		[API_RESOURCES.ADMIN.ADMINS]: undefined
	});
}

export function getProjectById(projectId) {
	return createApiReadAction(GET_PROJECT_BY_ID, {
		[API_RESOURCES.ADMIN.ADMINS]: undefined,
		[API_RESOURCES.ADMIN.PROJECTS]: projectId
	});
}

export function getUserInfoById(userId, isValidate = false) {
	return createApiReadAction(
		GET_USER_INFO_BY_ID,
		{
			[API_RESOURCES.ADMIN.USERS]: userId
		},
		undefined,
		{isValidate}
	);
}

export function clearProject(projectId) {
	return {
		type: CLEAR_PROJECT,
		projectId
	};
}

export function createAdminUser(data) {
	return createApiCreateAction(
		CREATE_ADMIN_USER,
		{
			[API_RESOURCES.ADMIN.ADMINS]: undefined
		},
		data
	);
}

export function patchAdminUser(userId, data) {
	return createApiUpdateAction(
		PATCH_ADMIN_USER,
		{
			[API_RESOURCES.ADMIN.ADMINS]: userId
		},
		data,
		HTTP_REQUEST_METHODS.PATCH
	);
}

export function updateProjectStore(projectId, property, value, userId) {
	return {
		type: UPDATE_PROJECTS_STORE,
		projectId,
		property,
		value,
		userId
	};
}

export function clearUserInfo(userId) {
	return {
		type: CLEAR_USER_INFO,
		userId
	};
}

export function updateProject(projectId, data) {
	return createApiUpdateAction(
		UPDATE_PROJECT,
		{
			[API_RESOURCES.ADMIN.ADMINS]: undefined,
			[API_RESOURCES.ADMIN.PROJECTS]: projectId
		},
		data
	);
}

export function resetProject(projectId) {
	return {
		type: RESET_PROJECT,
		projectId
	};
}

export function addUserToProject(projectId, user) {
	return {
		type: ADD_USER_PROJECT,
		projectId,
		user
	};
}
